import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { ADMIN } from "@/constants/testIds";
import {
  UtensilsCrossed, Image, Settings, Plus, Edit2, Trash2,
  Save, X, UploadCloud, BarChart2, CheckCircle, XCircle, Star
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const getImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BACKEND_URL}/api/files/${path}`;
};

// ---- Image Uploader ----
function ImageUploader({ onUpload, current, label, compact = false }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpload(data.path);
      toast.success("Imagem enviada!");
    } catch {
      toast.error("Erro ao enviar imagem.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className={compact ? "flex items-center gap-3" : ""}>
      {label && !compact && (
        <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-2">{label}</label>
      )}
      {current && (
        <div className={`${compact ? "h-10 w-10" : "h-32 mb-2"} overflow-hidden rounded`}>
          <img src={getImageUrl(current)} alt="" className="h-full w-full object-cover" />
        </div>
      )}
      <div>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" id={`uploader-${label}`} />
        <label
          htmlFor={`uploader-${label}`}
          data-testid={ADMIN.uploadBtn}
          className={`cursor-pointer flex items-center gap-2 ${compact ? "text-xs px-3 py-1.5" : "text-sm px-4 py-2"} border border-[#0077B6]/40 text-[#0077B6] hover:bg-[#0077B6]/5 transition-colors font-lato`}
        >
          <UploadCloud size={compact ? 12 : 14} />
          {uploading ? "A enviar..." : compact ? "Alterar" : "Enviar imagem"}
        </label>
      </div>
    </div>
  );
}

// ---- Overview Section ----
function OverviewSection({ stats, settings }) {
  return (
    <div>
      <div className="mb-8">
        <h2 className="font-cormorant text-3xl text-[#4A4A4A]">Dashboard</h2>
        <p className="font-lato text-sm text-gray-500 mt-1">Bem-vindo de volta, Administrador.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        {[
          { label: "Pratos", value: stats.dishes, icon: UtensilsCrossed, color: "text-[#0077B6]" },
          { label: "Categorias", value: stats.categories, icon: BarChart2, color: "text-[#C00000]" },
          { label: "Galeria", value: stats.gallery, icon: Image, color: "text-gray-600" },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white border border-gray-100 p-6 rounded shadow-sm flex items-center gap-4">
            <div className={`${color}`}><Icon size={28} /></div>
            <div>
              <p className="font-cormorant text-4xl text-[#4A4A4A] font-semibold">{value ?? "—"}</p>
              <p className="font-lato text-xs text-gray-400 tracking-widest uppercase mt-0.5">{label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-100 p-6 rounded shadow-sm">
        <h3 className="font-lato text-sm font-semibold text-gray-700 mb-4">Informações Rápidas</h3>
        <div className="grid grid-cols-2 gap-4 text-sm font-lato">
          <div>
            <span className="text-gray-500">Reservas: </span>
            <a href={settings?.reservation_url} target="_blank" rel="noreferrer" className="text-[#0077B6] hover:underline truncate">
              {settings?.reservation_url ? "TheFork" : "Não configurado"}
            </a>
          </div>
          <div>
            <span className="text-gray-500">Telefone: </span>
            <span className="text-[#4A4A4A]">{settings?.phone || "—"}</span>
          </div>
          <div>
            <span className="text-gray-500">Email: </span>
            <span className="text-[#4A4A4A]">{settings?.email || "—"}</span>
          </div>
          <div>
            <span className="text-gray-500">Horário: </span>
            <span className="text-[#4A4A4A]">Seg–Sáb: 12–14:30 / 19–22h</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Content Section ----
function ContentSection({ settings, reload }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) setForm({
      hero_title_pt: settings.hero_title_pt || "",
      hero_title_en: settings.hero_title_en || "",
      hero_subtitle_pt: settings.hero_subtitle_pt || "",
      hero_subtitle_en: settings.hero_subtitle_en || "",
      hero_image: settings.hero_image || "",
      about_title_pt: settings.about_title_pt || "",
      about_title_en: settings.about_title_en || "",
      about_text_pt: settings.about_text_pt || "",
      about_text_en: settings.about_text_en || "",
      about_image: settings.about_image || "",
    });
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, form, { withCredentials: true });
      toast.success("Conteúdo guardado!");
      reload();
    } catch { toast.error("Erro ao guardar."); }
    finally { setSaving(false); }
  };

  const F = ({ label, field, multiline, lang, placeholder }) => (
    <div>
      <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-1.5">
        {label} <span className={`px-1.5 py-0.5 rounded text-white text-[10px] ${lang === "pt" ? "bg-green-600" : "bg-blue-600"}`}>{lang === "pt" ? "PT" : "EN"}</span>
      </label>
      {multiline ? (
        <textarea
          value={form[field] || ""}
          onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
          rows={4}
          placeholder={placeholder}
          className="w-full border border-gray-200 px-3 py-2.5 text-sm font-lato text-[#4A4A4A] focus:outline-none focus:border-[#0077B6] resize-none"
        />
      ) : (
        <input
          type="text"
          value={form[field] || ""}
          onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
          placeholder={placeholder}
          className="w-full border border-gray-200 px-3 py-2.5 text-sm font-lato text-[#4A4A4A] focus:outline-none focus:border-[#0077B6]"
        />
      )}
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-cormorant text-3xl text-[#4A4A4A]">Conteúdo</h2>
          <p className="font-lato text-sm text-gray-500 mt-1">Textos e imagens da página principal</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid={ADMIN.saveBtn}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#0077B6] text-white font-lato text-xs tracking-widest uppercase hover:bg-[#005f8f] transition-colors disabled:opacity-50"
        >
          <Save size={14} />
          {saving ? "A guardar..." : "Guardar"}
        </button>
      </div>

      <div className="space-y-8">
        {/* Hero Section */}
        <div className="bg-white border border-gray-100 p-6 rounded shadow-sm">
          <h3 className="font-lato text-sm font-semibold text-gray-700 mb-5 flex items-center gap-2">
            <span className="w-1 h-4 bg-[#0077B6] rounded inline-block" /> Secção Hero
          </h3>
          <div className="grid md:grid-cols-2 gap-5">
            <F label="Título" field="hero_title_pt" lang="pt" />
            <F label="Title" field="hero_title_en" lang="en" />
            <F label="Subtítulo" field="hero_subtitle_pt" lang="pt" />
            <F label="Subtitle" field="hero_subtitle_en" lang="en" />
          </div>
          <div className="mt-5">
            <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-2">Imagem Hero</label>
            <div className="flex items-start gap-4">
              {form.hero_image && (
                <img src={getImageUrl(form.hero_image)} alt="" className="h-24 w-36 object-cover rounded" />
              )}
              <div>
                <ImageUploader
                  onUpload={path => setForm(p => ({ ...p, hero_image: path }))}
                  label="hero_img"
                  compact
                />
                <input
                  type="text"
                  value={form.hero_image || ""}
                  onChange={e => setForm(p => ({ ...p, hero_image: e.target.value }))}
                  placeholder="URL externo ou path"
                  className="mt-2 w-full border border-gray-200 px-3 py-2 text-xs font-lato text-gray-600 focus:outline-none focus:border-[#0077B6]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="bg-white border border-gray-100 p-6 rounded shadow-sm">
          <h3 className="font-lato text-sm font-semibold text-gray-700 mb-5 flex items-center gap-2">
            <span className="w-1 h-4 bg-[#C00000] rounded inline-block" /> Sobre o Restaurante
          </h3>
          <div className="grid md:grid-cols-2 gap-5">
            <F label="Título" field="about_title_pt" lang="pt" />
            <F label="Title" field="about_title_en" lang="en" />
            <F label="Texto" field="about_text_pt" lang="pt" multiline />
            <F label="Text" field="about_text_en" lang="en" multiline />
          </div>
          <div className="mt-5">
            <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-2">Imagem</label>
            <div className="flex items-start gap-4">
              {form.about_image && (
                <img src={getImageUrl(form.about_image)} alt="" className="h-24 w-36 object-cover rounded" />
              )}
              <div>
                <ImageUploader
                  onUpload={path => setForm(p => ({ ...p, about_image: path }))}
                  label="about_img"
                  compact
                />
                <input
                  type="text"
                  value={form.about_image || ""}
                  onChange={e => setForm(p => ({ ...p, about_image: e.target.value }))}
                  placeholder="URL externo ou path"
                  className="mt-2 w-full border border-gray-200 px-3 py-2 text-xs font-lato text-gray-600 focus:outline-none focus:border-[#0077B6]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Menu Section ----
function MenuSection() {
  const [categories, setCategories] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [showCatModal, setShowCatModal] = useState(false);
  const [showDishModal, setShowDishModal] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [editingDish, setEditingDish] = useState(null);
  const [catForm, setCatForm] = useState({ name_pt: "", name_en: "", order: 0 });
  const [dishForm, setDishForm] = useState({ category_id: "", name_pt: "", name_en: "", description_pt: "", description_en: "", price: "", image_path: "", available: true, featured: false, order: 0 });

  const load = async () => {
    const [c, d] = await Promise.all([
      axios.get(`${API}/categories`, { withCredentials: true }),
      axios.get(`${API}/dishes`, { withCredentials: true }),
    ]);
    setCategories(c.data);
    setDishes(d.data);
    if (!activeCategory && c.data.length) setActiveCategory(c.data[0].id);
  };

  useEffect(() => { load(); }, []);

  const saveCat = async () => {
    try {
      if (editingCat) {
        await axios.put(`${API}/categories/${editingCat.id}`, catForm, { withCredentials: true });
        toast.success("Categoria atualizada!");
      } else {
        await axios.post(`${API}/categories`, catForm, { withCredentials: true });
        toast.success("Categoria criada!");
      }
      setShowCatModal(false);
      setEditingCat(null);
      load();
    } catch { toast.error("Erro ao guardar categoria."); }
  };

  const deleteCat = async (id) => {
    if (!window.confirm("Eliminar esta categoria e todos os seus pratos?")) return;
    await axios.delete(`${API}/categories/${id}`, { withCredentials: true });
    toast.success("Categoria eliminada.");
    if (activeCategory === id) setActiveCategory(null);
    load();
  };

  const saveDish = async () => {
    try {
      const payload = { ...dishForm, price: parseFloat(dishForm.price) || 0 };
      if (editingDish) {
        await axios.put(`${API}/dishes/${editingDish.id}`, payload, { withCredentials: true });
        toast.success("Prato atualizado!");
      } else {
        await axios.post(`${API}/dishes`, payload, { withCredentials: true });
        toast.success("Prato criado!");
      }
      setShowDishModal(false);
      setEditingDish(null);
      load();
    } catch { toast.error("Erro ao guardar prato."); }
  };

  const deleteDish = async (id) => {
    if (!window.confirm("Eliminar este prato?")) return;
    await axios.delete(`${API}/dishes/${id}`, { withCredentials: true });
    toast.success("Prato eliminado.");
    load();
  };

  const openEditDish = (dish) => {
    setEditingDish(dish);
    setDishForm({ ...dish, price: dish.price?.toString() || "" });
    setShowDishModal(true);
  };

  const openNewDish = () => {
    setEditingDish(null);
    setDishForm({ category_id: activeCategory || "", name_pt: "", name_en: "", description_pt: "", description_en: "", price: "", image_path: "", available: true, featured: false, order: 0 });
    setShowDishModal(true);
  };

  const catDishes = dishes.filter(d => d.category_id === activeCategory);

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-cormorant text-3xl text-[#4A4A4A]">Ementa</h2>
          <p className="font-lato text-sm text-gray-500 mt-1">Gerir categorias e pratos</p>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-6">
        {/* Categories sidebar */}
        <div className="md:col-span-1">
          <div className="bg-white border border-gray-100 rounded shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-lato text-xs tracking-widest uppercase text-gray-500">Categorias</span>
              <button
                onClick={() => { setEditingCat(null); setCatForm({ name_pt: "", name_en: "", order: 0 }); setShowCatModal(true); }}
                data-testid={ADMIN.addCategoryBtn}
                className="text-[#0077B6] hover:text-[#005f8f]"
              >
                <Plus size={16} />
              </button>
            </div>
            <ul className="space-y-1">
              {categories.map(cat => (
                <li key={cat.id} className="group flex items-center justify-between">
                  <button
                    onClick={() => setActiveCategory(cat.id)}
                    className={`flex-1 text-left px-2 py-1.5 rounded text-sm font-lato transition-colors ${activeCategory === cat.id ? "bg-[#0077B6]/10 text-[#0077B6]" : "text-[#4A4A4A] hover:bg-gray-50"}`}
                  >
                    {cat.name_pt}
                  </button>
                  <div className="hidden group-hover:flex gap-1">
                    <button onClick={() => { setEditingCat(cat); setCatForm({ name_pt: cat.name_pt, name_en: cat.name_en, order: cat.order }); setShowCatModal(true); }} className="text-gray-400 hover:text-[#0077B6]"><Edit2 size={13} /></button>
                    <button onClick={() => deleteCat(cat.id)} className="text-gray-400 hover:text-red-500"><Trash2 size={13} /></button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Dishes */}
        <div className="md:col-span-3">
          <div className="bg-white border border-gray-100 rounded shadow-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <span className="font-lato text-sm font-semibold text-gray-700">
                {categories.find(c => c.id === activeCategory)?.name_pt || "Pratos"}
                <span className="ml-2 text-gray-400 font-normal text-xs">({catDishes.length})</span>
              </span>
              <button
                onClick={openNewDish}
                data-testid={ADMIN.addDishBtn}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0077B6] text-white font-lato text-xs uppercase tracking-wider hover:bg-[#005f8f] transition-colors"
              >
                <Plus size={13} /> Novo Prato
              </button>
            </div>

            {catDishes.length === 0 ? (
              <div className="py-12 text-center text-gray-400">
                <UtensilsCrossed size={32} className="mx-auto mb-2" />
                <p className="font-lato text-sm">Sem pratos nesta categoria</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {catDishes.map(dish => (
                  <div key={dish.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 group">
                    <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded bg-gray-100">
                      {dish.image_path ? (
                        <img src={getImageUrl(dish.image_path)} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-gray-300">
                          <UtensilsCrossed size={16} />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-lato text-sm text-[#4A4A4A] font-medium">{dish.name_pt}</span>
                        {dish.featured && <Star size={12} className="text-yellow-500 fill-yellow-500" />}
                        {dish.available ? <CheckCircle size={12} className="text-green-500" /> : <XCircle size={12} className="text-red-400" />}
                      </div>
                      <p className="font-lato text-xs text-gray-400 truncate mt-0.5">{dish.description_pt}</p>
                    </div>
                    <span className="font-cormorant text-lg text-[#C00000] font-semibold">{dish.price?.toFixed(2)}€</span>
                    <div className="hidden group-hover:flex gap-2">
                      <button onClick={() => openEditDish(dish)} data-testid={ADMIN.editBtn} className="text-gray-400 hover:text-[#0077B6]"><Edit2 size={15} /></button>
                      <button onClick={() => deleteDish(dish.id)} data-testid={ADMIN.deleteBtn} className="text-gray-400 hover:text-red-500"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Category Modal */}
      {showCatModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-cormorant text-2xl text-[#4A4A4A]">{editingCat ? "Editar Categoria" : "Nova Categoria"}</h3>
              <button onClick={() => setShowCatModal(false)}><X size={20} className="text-gray-400" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-1.5">Nome (PT)</label>
                <input value={catForm.name_pt} onChange={e => setCatForm(p => ({ ...p, name_pt: e.target.value }))} className="w-full border border-gray-200 px-3 py-2.5 text-sm font-lato focus:outline-none focus:border-[#0077B6]" />
              </div>
              <div>
                <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-1.5">Name (EN)</label>
                <input value={catForm.name_en} onChange={e => setCatForm(p => ({ ...p, name_en: e.target.value }))} className="w-full border border-gray-200 px-3 py-2.5 text-sm font-lato focus:outline-none focus:border-[#0077B6]" />
              </div>
              <div>
                <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-1.5">Ordem</label>
                <input type="number" value={catForm.order} onChange={e => setCatForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} className="w-full border border-gray-200 px-3 py-2.5 text-sm font-lato focus:outline-none focus:border-[#0077B6]" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowCatModal(false)} className="px-4 py-2 font-lato text-sm text-gray-600 border border-gray-200 hover:bg-gray-50">Cancelar</button>
              <button onClick={saveCat} className="px-5 py-2 bg-[#0077B6] text-white font-lato text-sm hover:bg-[#005f8f] flex items-center gap-2"><Save size={14} /> Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Dish Modal */}
      {showDishModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-2xl p-6 shadow-2xl my-4">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-cormorant text-2xl text-[#4A4A4A]">{editingDish ? "Editar Prato" : "Novo Prato"}</h3>
              <button onClick={() => setShowDishModal(false)}><X size={20} className="text-gray-400" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-1.5">Categoria</label>
                <select value={dishForm.category_id} onChange={e => setDishForm(p => ({ ...p, category_id: e.target.value }))} className="w-full border border-gray-200 px-3 py-2.5 text-sm font-lato focus:outline-none focus:border-[#0077B6]">
                  <option value="">— Selecionar —</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name_pt}</option>)}
                </select>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-1.5">Nome (PT)</label>
                  <input value={dishForm.name_pt} onChange={e => setDishForm(p => ({ ...p, name_pt: e.target.value }))} className="w-full border border-gray-200 px-3 py-2.5 text-sm font-lato focus:outline-none focus:border-[#0077B6]" />
                </div>
                <div>
                  <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-1.5">Name (EN)</label>
                  <input value={dishForm.name_en} onChange={e => setDishForm(p => ({ ...p, name_en: e.target.value }))} className="w-full border border-gray-200 px-3 py-2.5 text-sm font-lato focus:outline-none focus:border-[#0077B6]" />
                </div>
                <div>
                  <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-1.5">Descrição (PT)</label>
                  <textarea value={dishForm.description_pt} onChange={e => setDishForm(p => ({ ...p, description_pt: e.target.value }))} rows={2} className="w-full border border-gray-200 px-3 py-2.5 text-sm font-lato focus:outline-none focus:border-[#0077B6] resize-none" />
                </div>
                <div>
                  <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-1.5">Description (EN)</label>
                  <textarea value={dishForm.description_en} onChange={e => setDishForm(p => ({ ...p, description_en: e.target.value }))} rows={2} className="w-full border border-gray-200 px-3 py-2.5 text-sm font-lato focus:outline-none focus:border-[#0077B6] resize-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-1.5">Preço (€)</label>
                  <input type="number" step="0.01" value={dishForm.price} onChange={e => setDishForm(p => ({ ...p, price: e.target.value }))} className="w-full border border-gray-200 px-3 py-2.5 text-sm font-lato focus:outline-none focus:border-[#0077B6]" />
                </div>
                <div>
                  <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-1.5">Ordem</label>
                  <input type="number" value={dishForm.order} onChange={e => setDishForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} className="w-full border border-gray-200 px-3 py-2.5 text-sm font-lato focus:outline-none focus:border-[#0077B6]" />
                </div>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={dishForm.available} onChange={e => setDishForm(p => ({ ...p, available: e.target.checked }))} className="accent-[#0077B6]" />
                  <span className="font-lato text-sm text-[#4A4A4A]">Disponível</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={dishForm.featured} onChange={e => setDishForm(p => ({ ...p, featured: e.target.checked }))} className="accent-[#0077B6]" />
                  <span className="font-lato text-sm text-[#4A4A4A]">Destaque</span>
                </label>
              </div>
              <div>
                <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-2">Fotografia do Prato</label>
                <div className="flex items-start gap-4">
                  {dishForm.image_path && (
                    <img src={getImageUrl(dishForm.image_path)} alt="" className="h-20 w-24 object-cover rounded" />
                  )}
                  <div>
                    <ImageUploader
                      onUpload={path => setDishForm(p => ({ ...p, image_path: path }))}
                      label={`dish_${editingDish?.id || "new"}`}
                      compact
                    />
                    <input type="text" value={dishForm.image_path} onChange={e => setDishForm(p => ({ ...p, image_path: e.target.value }))} placeholder="URL externo ou path" className="mt-2 w-full border border-gray-200 px-3 py-2 text-xs font-lato text-gray-600 focus:outline-none focus:border-[#0077B6]" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowDishModal(false)} className="px-4 py-2 font-lato text-sm text-gray-600 border border-gray-200 hover:bg-gray-50">Cancelar</button>
              <button onClick={saveDish} className="px-5 py-2 bg-[#0077B6] text-white font-lato text-sm hover:bg-[#005f8f] flex items-center gap-2"><Save size={14} /> Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Gallery Section ----
function GallerySection() {
  const [gallery, setGallery] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const { data } = await axios.get(`${API}/gallery`, { withCredentials: true });
    setGallery(data);
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      await axios.post(`${API}/gallery`, { image_path: data.path, caption_pt: "", caption_en: "", order: gallery.length }, { withCredentials: true });
      toast.success("Imagem adicionada à galeria!");
      load();
    } catch { toast.error("Erro ao adicionar imagem."); }
    finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteImg = async (id) => {
    if (!window.confirm("Remover esta imagem da galeria?")) return;
    await axios.delete(`${API}/gallery/${id}`, { withCredentials: true });
    toast.success("Imagem removida.");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-cormorant text-3xl text-[#4A4A4A]">Galeria</h2>
          <p className="font-lato text-sm text-gray-500 mt-1">{gallery.length} imagens</p>
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" id="gallery-upload" />
          <label
            htmlFor="gallery-upload"
            data-testid={ADMIN.uploadBtn}
            className="cursor-pointer flex items-center gap-2 px-5 py-2.5 bg-[#0077B6] text-white font-lato text-xs tracking-widest uppercase hover:bg-[#005f8f] transition-colors"
          >
            <UploadCloud size={14} />
            {uploading ? "A enviar..." : "Adicionar Imagem"}
          </label>
        </div>
      </div>

      {gallery.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded py-20 text-center text-gray-400">
          <Image size={40} className="mx-auto mb-3" />
          <p className="font-lato text-sm">Galeria vazia — adicione imagens acima</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {gallery.map(img => (
            <div key={img.id} className="relative group aspect-square overflow-hidden rounded bg-gray-100">
              <img
                src={getImageUrl(img.image_path)}
                alt={img.caption_pt}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button
                  onClick={() => deleteImg(img.id)}
                  data-testid={ADMIN.deleteBtn}
                  className="bg-red-500 text-white p-2 rounded hover:bg-red-600 transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              </div>
              {img.caption_pt && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
                  <p className="font-lato text-xs text-white truncate">{img.caption_pt}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Settings Section ----
function SettingsSection({ settings, reload }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) setForm({ ...settings });
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, form, { withCredentials: true });
      toast.success("Configurações guardadas!");
      reload();
    } catch { toast.error("Erro ao guardar configurações."); }
    finally { setSaving(false); }
  };

  const Field = ({ label, field, type = "text", multiline, placeholder, hint }) => (
    <div>
      <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-1.5">{label}</label>
      {hint && <p className="text-xs text-gray-400 mb-1.5">{hint}</p>}
      {multiline ? (
        <textarea
          value={form[field] || ""}
          onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
          rows={3}
          placeholder={placeholder}
          className="w-full border border-gray-200 px-3 py-2.5 text-sm font-lato text-[#4A4A4A] focus:outline-none focus:border-[#0077B6] resize-none"
        />
      ) : (
        <input
          type={type}
          value={form[field] || ""}
          onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
          placeholder={placeholder}
          className="w-full border border-gray-200 px-3 py-2.5 text-sm font-lato text-[#4A4A4A] focus:outline-none focus:border-[#0077B6]"
        />
      )}
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-cormorant text-3xl text-[#4A4A4A]">Configurações</h2>
          <p className="font-lato text-sm text-gray-500 mt-1">Informações, horários, redes sociais e SEO</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid={ADMIN.saveBtn}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#0077B6] text-white font-lato text-xs tracking-widest uppercase hover:bg-[#005f8f] transition-colors disabled:opacity-50"
        >
          <Save size={14} />
          {saving ? "A guardar..." : "Guardar Tudo"}
        </button>
      </div>

      <div className="space-y-6">
        {/* Contact */}
        <div className="bg-white border border-gray-100 p-6 rounded shadow-sm">
          <h3 className="font-lato text-sm font-semibold text-gray-700 mb-5 flex items-center gap-2">
            <span className="w-1 h-4 bg-[#0077B6] rounded" /> Informações de Contacto
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Morada" field="address" placeholder="Av. João Paulo II L2, 8500-780 Portimão" />
            <Field label="Telefone" field="phone" placeholder="+351 282 074 993" />
            <Field label="Email" field="email" type="email" placeholder="info@quintoimperio.pt" />
            <Field label="Link de Reservas" field="reservation_url" placeholder="https://www.thefork.pt/..." />
          </div>
        </div>

        {/* Hours */}
        <div className="bg-white border border-gray-100 p-6 rounded shadow-sm">
          <h3 className="font-lato text-sm font-semibold text-gray-700 mb-5 flex items-center gap-2">
            <span className="w-1 h-4 bg-[#C00000] rounded" /> Horários
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-1.5">
                Horários <span className="px-1.5 py-0.5 rounded text-white text-[10px] bg-green-600">PT</span>
              </label>
              <textarea value={form.hours_pt || ""} onChange={e => setForm(p => ({ ...p, hours_pt: e.target.value }))} rows={3} className="w-full border border-gray-200 px-3 py-2.5 text-sm font-lato text-[#4A4A4A] focus:outline-none focus:border-[#0077B6] resize-none" placeholder="Segunda a Sábado: 12:00-14:30 e 19:00-22:00&#10;Domingo: Encerrado" />
            </div>
            <div>
              <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-1.5">
                Hours <span className="px-1.5 py-0.5 rounded text-white text-[10px] bg-blue-600">EN</span>
              </label>
              <textarea value={form.hours_en || ""} onChange={e => setForm(p => ({ ...p, hours_en: e.target.value }))} rows={3} className="w-full border border-gray-200 px-3 py-2.5 text-sm font-lato text-[#4A4A4A] focus:outline-none focus:border-[#0077B6] resize-none" placeholder="Monday to Saturday: 12:00-14:30 and 19:00-22:00&#10;Sunday: Closed" />
            </div>
          </div>
        </div>

        {/* Maps */}
        <div className="bg-white border border-gray-100 p-6 rounded shadow-sm">
          <h3 className="font-lato text-sm font-semibold text-gray-700 mb-5 flex items-center gap-2">
            <span className="w-1 h-4 bg-gray-600 rounded" /> Google Maps
          </h3>
          <Field label="URL de Embed do Google Maps" field="maps_embed_url" placeholder="https://www.google.com/maps/embed?pb=..." hint="Cole o URL do iframe de incorporação do Google Maps" />
        </div>

        {/* Logos */}
        <div className="bg-white border border-gray-100 p-6 rounded shadow-sm">
          <h3 className="font-lato text-sm font-semibold text-gray-700 mb-5 flex items-center gap-2">
            <span className="w-1 h-4 bg-[#0077B6] rounded" /> Logótipos
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-2">Logótipo Header (horizontal)</label>
              <div className="flex items-start gap-3">
                {form.logo_header && <img src={getImageUrl(form.logo_header)} alt="" className="h-12 w-auto object-contain border border-gray-100 p-1 rounded" />}
                <div>
                  <ImageUploader onUpload={path => setForm(p => ({ ...p, logo_header: path }))} label="logo_h" compact />
                  <input type="text" value={form.logo_header || ""} onChange={e => setForm(p => ({ ...p, logo_header: e.target.value }))} placeholder="URL ou path" className="mt-1.5 w-full border border-gray-200 px-3 py-2 text-xs font-lato text-gray-600 focus:outline-none focus:border-[#0077B6]" />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs font-lato tracking-widest uppercase text-gray-500 mb-2">Logótipo Footer (quadrado)</label>
              <div className="flex items-start gap-3">
                {form.logo_footer && <img src={getImageUrl(form.logo_footer)} alt="" className="h-12 w-auto object-contain border border-gray-100 p-1 rounded" />}
                <div>
                  <ImageUploader onUpload={path => setForm(p => ({ ...p, logo_footer: path }))} label="logo_f" compact />
                  <input type="text" value={form.logo_footer || ""} onChange={e => setForm(p => ({ ...p, logo_footer: e.target.value }))} placeholder="URL ou path" className="mt-1.5 w-full border border-gray-200 px-3 py-2 text-xs font-lato text-gray-600 focus:outline-none focus:border-[#0077B6]" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white border border-gray-100 p-6 rounded shadow-sm">
          <h3 className="font-lato text-sm font-semibold text-gray-700 mb-5 flex items-center gap-2">
            <span className="w-1 h-4 bg-[#C00000] rounded" /> Redes Sociais
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Facebook URL" field="social_facebook" placeholder="https://facebook.com/..." />
            <Field label="Instagram URL" field="social_instagram" placeholder="https://instagram.com/..." />
            <Field label="TripAdvisor URL" field="social_tripadvisor" placeholder="https://tripadvisor.pt/..." />
          </div>
        </div>

        {/* SEO */}
        <div className="bg-white border border-gray-100 p-6 rounded shadow-sm">
          <h3 className="font-lato text-sm font-semibold text-gray-700 mb-5 flex items-center gap-2">
            <span className="w-1 h-4 bg-gray-600 rounded" /> SEO
          </h3>
          <div className="space-y-4">
            <Field label="Título SEO" field="seo_title" placeholder="O Quinto Império - Restaurante Mediterrânico em Portimão" />
            <Field label="Descrição SEO" field="seo_description" multiline placeholder="Restaurante de cozinha mediterrânica em Portimão, Algarve..." />
            <Field label="Palavras-chave" field="seo_keywords" placeholder="restaurante portimão, algarve, mediterrânico..." />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Main Dashboard ----
export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [settings, setSettings] = useState({});
  const [stats, setStats] = useState({ dishes: 0, categories: 0, gallery: 0 });

  const loadSettings = async () => {
    const { data } = await axios.get(`${API}/settings`);
    setSettings(data);
  };

  const loadStats = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(data);
    } catch { }
  };

  useEffect(() => {
    loadSettings();
    loadStats();
  }, []);

  const reload = () => { loadSettings(); loadStats(); };

  return (
    <AdminLayout activeTab={activeTab} setActiveTab={setActiveTab}>
      <div data-testid={ADMIN.dashboard} className="p-6 md:p-8 max-w-6xl">
        {activeTab === "overview" && <OverviewSection stats={stats} settings={settings} />}
        {activeTab === "content" && <ContentSection settings={settings} reload={reload} />}
        {activeTab === "menu" && <MenuSection />}
        {activeTab === "gallery" && <GallerySection />}
        {activeTab === "settings" && <SettingsSection settings={settings} reload={reload} />}
      </div>
    </AdminLayout>
  );
}
